import clone from 'clone';
import moment from 'moment-timezone';
import { Gizmo } from '../../gizmos';
import {
  IGizmo,
  IIndex,
  IIndexColumn,
  IIndexFilter,
  IIndexOperation,
} from '../../types';
import { gnov } from '../../utils';
import CommonBinder from '../common-binder';
import { Subject } from 'rxjs';

class IndexBinder extends CommonBinder {
  /**
   * Index data
   */
  private _index?: IIndex;
  /**
   * List of filters currently applied to the index
   */
  private _filters: IIndexFilter[] = [];
  private _initializing: boolean = false;
  public index: Subject<IIndex>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.index = new Subject<IIndex>();
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      const state = this._loadIndexState();
      let page: number | undefined;
      let pageSize: number | undefined;

      this._initializing = true;

      if (
        config.ops?.index?.rowType === 'form.registers' &&
        state?.pagination?.page &&
        state?.pagination?.pageSize
      ) {
        page = state.pagination.page;
        pageSize = state.pagination.pageSize;
      }
      if (config.ops?.index?.rowType === 'forms') {
        this._clearIndexState();
      }

      await this.pageChange({
        page,
        pageSize,
      });

      this._initializing = false;
      resolve();
    });
  };

  /**
   * Get the current index data
   *
   * @returns {IIndex} index data
   */
  public getIndex = (): IIndex | undefined => {
    return this._index;
  };

  /**
   * Get the current filters applied to the index
   * @returns {IIndexFilter[]} filters applied to the index
   */
  public getFilters = (): IIndexFilter[] => {
    return this._filters;
  };

  public setIndexType = (type: 'table' | 'map' | 'kanban') => {
    if (this._index) {
      this._index.type = type;

      // TODO: should we send it on the Observable?
    }
  };

  /**
   * Get the cell value that will be rendered on the platform layer
   *
   * @param {IIndexColumn} column column's configuration
   * @param {any} row data row
   * @returns object with the info that's going to be rendered
   */
  public getCell = ({ column, row }: IGetCellParams) => {
    if (column.path) {
      const value = gnov(row, column.path);
      return { v: value };
    }
    if (
      column.type === 'operations' &&
      column.operations &&
      Array.isArray(column.operations)
    ) {
      return column.operations.map((operation: IIndexOperation) => {
        return { v: operation.label, op: operation };
      });
    } else if (column.type === 'status' && row.status && column.status) {
      const statusFid = row.status;
      const status = column.status.find(s => s.f_id === statusFid);
      return { v: status ? status.title : '', status };
    } else if (column.type === 'user' && row.username) {
      return { v: row.username };
    }

    if (column.gizmo) {
      let value = gnov(row, `answers.${column.gizmo.fid}.0.value`);

      if (
        column.gizmo.type === 'date_field' &&
        value &&
        (typeof value === 'string' ||
          typeof value === 'number' ||
          value instanceof Date)
      ) {
        const momentDate = moment(value);
        if (momentDate.isValid()) {
          value = momentDate.format('DD/MM/YYYY');
        }
      }

      if (value !== undefined) {
        return { v: value };
      }
    }

    return undefined;
  };

  /**
   * When user has clicked or pressed on a cell this function will be called by the platform layer
   *
   * @param {IIndexColumn} column column's configuration
   * @param {any} row data row
   */
  public cellInteraction = ({
    column,
    row,
    action,
    operation,
    params,
  }: ICellInteraction): Promise<any> => {
    return new Promise<any>(async resolve => {
      if (column.path && column.systemType) {
        // If the column has a path and a systemType it means that it should navigate to
        // the route sd (system document).
        // TODO: when this is configured on the gizmo, the 'sd' in the path should come from there
        await this._gizmo.dispatchGizmoOperation({
          operation: 'navigation.link',
          params: {
            path: `/sd?fid=${row.id}`,
          },
        });
      } else if (column.path) {
        await this._gizmo.dispatchGizmoOperation({
          operation: 'navigation.link',
          params: {
            path: `/r?fid=${row.id}`,
          },
        });
      } else if (action && params) {
        return resolve(
          await this._gizmo.dispatchGizmoOperation({
            operation: action,
            params,
          })
        );
      } else if (column.gizmo) {
        await this._gizmo.dispatchGizmoOperation({
          operation: 'navigation.link',
          params: {
            path: `/rg?rgfid=${row.formId}&fid=${row.fid}`,
          },
        });
      } else if (column.type === 'operations' && operation) {
        if (operation.type === 'form.index') {
          let path = `/r?fid=${row.id}`;
          if (operation.path) {
            path = `${operation.path}?fid=${row.id}`;
          }

          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path,
            },
          });
        } else if (operation.type === 'form.stats') {
          let path = `/s?fid=${row.id}`;
          if (operation.path) {
            path = `${operation.path}?fid=${row.id}`;
          }

          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path,
            },
          });
        } else if (operation.type === 'register.show') {
          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path: `/rg?rgfid=${row.formId}&fid=${row.fid}&m=s`,
            },
          });
        } else if (operation.type === 'register.edit') {
          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path: `/rg?rgfid=${row.formId}&fid=${row.fid}`,
            },
          });
        } else if (operation.type === 'register.pdf') {
          return resolve(
            await this._gizmo.dispatchGizmoOperation({
              operation: 'pdf.download',
              params: {
                rgfid: row.formId,
                id: row.fid,
              },
            })
          );
        }
      }

      resolve(undefined);
    });
  };

  /**
   * Get page of elements from the server
   *
   * @param {number} page number of page we're going to retrieve from the server
   * @param {number} pageSize number of elements we're going to bring frmo the server
   *
   * @returns
   */
  public pageChange = ({
    page,
    pageSize,
  }: IPageChangeParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      const indexOps = config.ops?.index;
      const dao = this._gizmo.getDao();
      const params = this._gizmo.getParams();

      // TODO: get info from params
      if (dao && indexOps && indexOps.rowType === 'forms') {
        const index = await dao.getIndex({
          type: 'rootGizmo',
          gizmoConfig: config,
        });

        this._index = index;

        this.index.next(this._index);
      } else if (
        dao &&
        indexOps &&
        indexOps.rowType === 'form.registers' &&
        params.fid
      ) {
        if (this._filters.length === 0) {
          const index = await dao.getIndex({
            type: 'rootGizmoData',
            fid: params.fid,
            page,
            pageSize,
            gizmo: this._gizmo,
            gizmoConfig: config,
          });

          this._index = index;

          if (
            index?.rootGizmoConfigs &&
            Array.isArray(index.rootGizmoConfigs) &&
            index.rootGizmoConfigs.length > 0 &&
            // Only during initlization we're going to set the label on the navbar
            this._initializing
          ) {
            const label = index.rootGizmoConfigs[0].label;
            this._gizmo
              .getFoundation()
              ?.getGlobal()
              ?.setFeatures({
                singleFeatures: [
                  {
                    path: 'navbar.label',
                    value: label,
                  },
                ],
              });
          }

          this.index.next(this._index);
        } else if (this._filters.length === 1) {
          // Right now we're only supporting the status filter, when we add handling multiple
          // filters the implementation should change
          const statusFilter = this._filters[0];

          await this.statusFilter({
            statuses: statusFilter.keys as any[],
            page,
            pageSize,
          });

          this.index.next(this._index);
        }

        // Save pagination on the global cache
        this._saveIndexState();
      } else if (indexOps && indexOps.rowType === 'system') {
        this._index = await this._getSystemIndex();
        this.index.next(this._index);
      } else if (
        dao &&
        indexOps &&
        indexOps.rowType === 'system.documents' &&
        params.fid &&
        // TODO: add the different cases for system documents
        params.fid === 'users'
      ) {
        const index = await dao.getSystemIndex({
          type: params.fid,
          page,
          pageSize,
        });

        if (index) {
          this._index = index;
          this.index.next(this._index);
        }
      }

      resolve();
    });
  };

  /**
   * Get locations from the server, this works for both registers and users.
   *
   * @param {any} params params passed from the UI layer
   */
  public getLocations = async ({ params }: IGetLocations) => {
    return await this._gizmo.getDao()?.getLocations({
      params,
      gizmo: this._gizmo,
    });
  };

  /**
   * Get the system index configuration that will be rendered on front end
   * Should this function go to another file?
   */
  private _getSystemIndex = async () => {
    const index: IIndex = {
      fid: 'system-index',
      pagination: {
        page: 1,
        pageSize: 10,
        total: 1,
      },
      columns: [
        {
          fid: 'name',
          label: 'Form name',
          path: 'label',
          systemType: 'form',
        },
      ],
      rows: [
        {
          id: 'users',
          fid: 'users',
          label: 'Users',
        },
      ],
    };

    return index;
  };

  /**
   * Function intended to be used by the 'extra buttons' on the index
   *
   * @param {string} operation operation that's going to be sent
   * @param {any} params params with path that will be dispatched
   *
   * @returns {Promise<void>}
   */
  public dispatchOperation = ({
    operation,
    params,
  }: IDispatchOperationParams): Promise<any> => {
    return new Promise<any>(async resolve => {
      resolve(
        await this._gizmo.dispatchGizmoOperation({
          operation,
          params,
        })
      );
    });
  };

  /**
   * Function that will be called by the search bar
   *
   * @param {string} text text to search
   *
   * @returns
   */
  public textSearch = ({ text }: ITextSearch): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      return resolve(
        await this._gizmo.getDao()?.textSearch({
          text,
          gizmoConfig: this._gizmo.getConfig(),
          rootGizmoFid: this._gizmo.getParams().fid,
        })
      );
    });
  };

  /**
   * Function that will be called when searching on a column
   *
   * @param {string[]} selectedKeys keys selected by the user on the UI, that's how antd works
   * @param {IIndexColumn} column column's configuration
   *
   * @returns
   */
  public columnSearch = ({
    selectedKeys,
    column,
  }: IColumnSearch): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      if (column.gizmo) {
        const index = await this._gizmo.getDao()?.columnSearch({
          searchColumns: [
            {
              values: selectedKeys,
              gizmoFid: column.gizmo.fid,
            },
          ],
          rootGizmoFid: this._gizmo.getParams().fid,
          gizmoConfig: this._gizmo.getConfig(),
        });

        this.index.next(index);

        return resolve(index);
      }

      resolve(undefined);
    });
  };

  /**
   * Get the statuses that will be rendered on the UI. This statuses come from the form
   * configuration of the registers being rendered
   *
   * @returns
   */
  public getStatuses = () => {
    if (
      this._index?.rootGizmoConfigs &&
      Array.isArray(this._index.rootGizmoConfigs) &&
      this._index.rootGizmoConfigs.length > 0
    ) {
      const rootGizmoConfig: IGizmo = this._index.rootGizmoConfigs[0];

      return rootGizmoConfig.status?.filter(s => s.f_id !== 's0');
    }

    return undefined;
  };

  /**
   * Set status filter and send it to the server
   * @returns
   */
  public statusFilter = async ({ statuses, page, pageSize }: IStatusFilter) => {
    if (
      this._index?.rootGizmoConfigs &&
      Array.isArray(this._index.rootGizmoConfigs) &&
      this._index.rootGizmoConfigs.length > 0 &&
      statuses.length > 0
    ) {
      const rootGizmoConfig: IGizmo = this._index.rootGizmoConfigs[0];

      const filterSearch = statuses
        .map(s => `{{status}} == "${s}"`)
        .join(' OR ');

      const gizmoConfig = clone(this._gizmo.getConfig());
      if (gizmoConfig.ops) {
        gizmoConfig.ops.search = {
          rootGizmoFid: rootGizmoConfig.fid,
          minChars: 1,
        };
      }

      const index = await this._gizmo.getDao()?.searchFilledForms({
        gizmoConfig,
        rootGizmoFid: rootGizmoConfig.fid,
        filterSearch,
        page,
        pageSize,
      });

      if (index?.rows && Array.isArray(index.rows)) {
        this._index.rows = index.rows;
        this._index.pagination = index.pagination;

        const filter: IIndexFilter = {
          fid: 'status',
          type: 'status',
          keys: statuses,
        };

        const statusFilterIndex = this._filters.findIndex(
          s => s.fid === 'status'
        );
        if (statusFilterIndex !== -1) {
          this._filters[statusFilterIndex] = filter;
        } else {
          this._filters.push(filter);
        }

        return this._index;
      }
    } else {
      // We're removing the status filter
      this._filters = this._filters.filter(f => f.fid !== 'status');
      await this.pageChange({});
    }

    return undefined;
  };

  /**
   * Save the index state on the global cache
   */
  private _saveIndexState = () => {
    if (this._index) {
      const state: any = {
        pagination: this._index.pagination,
      };

      const config = this._gizmo.getConfig();
      const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
        type: 'rootGizmoFid',
      });

      this._gizmo
        .getFoundation()
        ?.getGlobal()
        ?.setCache({
          group: 'index_binder',
          key: `${rootGizmoFid}_${config.fid}`,
          value: state,
        });
    }
  };

  /**
   * Load the index state from the global cache
   * @returns state from cache
   */
  private _loadIndexState = () => {
    const config = this._gizmo.getConfig();
    const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
      type: 'rootGizmoFid',
    });

    const state = this._gizmo
      .getFoundation()
      ?.getGlobal()
      ?.getCache({
        group: 'index_binder',
        key: `${rootGizmoFid}_${config.fid}`,
      });

    return state;
  };

  /**
   * Clear the index state from the global cache
   */
  private _clearIndexState = () => {
    this._gizmo
      .getFoundation()
      ?.getGlobal()
      ?.deleteCache({
        group: 'index_binder',
      });
  };
}

interface IPageChangeParams {
  page?: number;
  pageSize?: number;
}

interface IGetLocations {
  params?: any;
}

interface IGetCellParams {
  column: IIndexColumn;
  row: any;
}

interface ICellInteraction {
  column: IIndexColumn;
  row: any;
  action?: 'pdf.download';
  operation?: IIndexOperation;
  params?: {
    rgfid: string;
    fid?: string;
    id?: string;
  };
}

interface IDispatchOperationParams {
  operation: 'navigation.link' | 'export.download';
  params: {
    path?: string;
    fid?: string;
  };
}

interface ITextSearch {
  text: string;
}

interface IColumnSearch {
  /**
   * Keys selected by the user on the UI, that's how antd works
   */
  selectedKeys: string[];
  /**
   * Column's configuration
   */
  column: IIndexColumn;
}

interface IStatusFilter {
  statuses: string[];
  page?: number;
  pageSize?: number;
}

export default IndexBinder;
